import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { TemperaturaLead } from "../types/cliente";

interface RegistrarLigacaoModalProps {
  clienteId: number;
  clienteNome: string;
  temperaturaAtual: TemperaturaLead;
  trigger?: React.ReactNode;
}

type ResultadoLigacao = 'ATENDIDO' | 'NAO_ATENDIDO' | 'CAIXA_POSTAL' | 'NUMERO_INVALIDO';

export function RegistrarLigacaoModal({
  clienteId,
  clienteNome,
  temperaturaAtual,
  trigger
}: RegistrarLigacaoModalProps) {
  const [open, setOpen] = useState(false);
  const [resultado, setResultado] = useState<ResultadoLigacao | ''>('');
  const [observacoes, setObservacoes] = useState('');
  const [temperaturaNova, setTemperaturaNova] = useState<TemperaturaLead>(temperaturaAtual);

  const queryClient = useQueryClient();

  const registrarLigacaoMutation = useMutation({
    mutationFn: async (data: {
      resultado: ResultadoLigacao;
      observacoes: string;
      temperaturaNova: TemperaturaLead;
    }) => {
      // Buscar dados atuais do cliente
      const { data: clienteAtual, error: fetchError } = await supabase
        .from('clientes')
        .select('ligacoes_realizadas')
        .eq('id', clienteId)
        .single();

      if (fetchError) throw fetchError;

      // Atualizar cliente com nova ligação
      const { error: updateError } = await supabase
        .from('clientes')
        .update({
          ligacoes_realizadas: (clienteAtual?.ligacoes_realizadas || 0) + 1,
          ultima_ligacao: new Date().toISOString(),
          temperatura_lead: data.temperaturaNova,
          status_atual: data.observacoes || undefined
        })
        .eq('id', clienteId);

      if (updateError) throw updateError;

      return { clienteId, ...data };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['relacionador-dashboard'] });
      toast.success(`Ligação registrada para ${clienteNome}`);
      setOpen(false);
      setResultado('');
      setObservacoes('');
      setTemperaturaNova(temperaturaAtual);
    },
    onError: (error: any) => {
      console.error('Erro ao registrar ligação:', error);
      toast.error('Erro ao registrar ligação. Tente novamente.');
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!resultado) {
      toast.error('Selecione o resultado da ligação');
      return;
    }

    registrarLigacaoMutation.mutate({
      resultado: resultado as ResultadoLigacao,
      observacoes,
      temperaturaNova
    });
  };

  const defaultTrigger = (
    <Button variant="outline" size="sm" className="gap-2">
      <Phone className="h-4 w-4" />
      Registrar Ligação
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneCall className="h-5 w-5" />
            Registrar Ligação
          </DialogTitle>
          <DialogDescription>
            Registre o resultado da ligação realizada para <strong>{clienteNome}</strong>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="resultado">Resultado da Ligação *</Label>
            <Select value={resultado} onValueChange={setResultado}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o resultado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ATENDIDO">Atendido</SelectItem>
                <SelectItem value="NAO_ATENDIDO">Não atendido</SelectItem>
                <SelectItem value="CAIXA_POSTAL">Caixa postal</SelectItem>
                <SelectItem value="NUMERO_INVALIDO">Número inválido</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="temperatura">Temperatura do Lead</Label>
            <Select value={temperaturaNova} onValueChange={(value: TemperaturaLead) => setTemperaturaNova(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a temperatura" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="QUENTE">🔥 Quente - Alta prioridade</SelectItem>
                <SelectItem value="MEDIO">🌡️ Médio - Média prioridade</SelectItem>
                <SelectItem value="FRIO">❄️ Frio - Baixa prioridade</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="observacoes">Observações da Ligação</Label>
            <Textarea
              id="observacoes"
              placeholder="Descreva o que aconteceu na ligação..."
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={registrarLigacaoMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={registrarLigacaoMutation.isPending || !resultado}
            >
              {registrarLigacaoMutation.isPending ? 'Registrando...' : 'Registrar Ligação'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


